import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Navbar.css';

function ChevronDownIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
      <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();
  const settingsRef = useRef(null);

  const handleLogout = () => {
    logout();
    setDrawerOpen(false);
    navigate('/login');
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setDrawerOpen(false);
    setSettingsOpen(false);
  }, [location]);

  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [drawerOpen]);

  // Close the Settings dropdown on outside click.
  useEffect(() => {
    if (!settingsOpen) return undefined;
    const handleClick = (e) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target)) setSettingsOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [settingsOpen]);

  const isHome = location.pathname === '/';
  const isSettingsPath = location.pathname.startsWith('/settings');

  // If already on home, scroll to section. Otherwise navigate home then scroll.
  const handleSectionLink = (e, hash) => {
    e.preventDefault();
    if (isHome) {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/');
      // After navigation, scroll once the DOM has settled
      setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 120);
    }
    setDrawerOpen(false);
  };

  // Marketing scroll-anchors — only meaningful on the (now-gated) home page.
  const sectionLinks = [
    { label: 'Features',     hash: '#features'     },
    { label: 'How It Works', hash: '#how-it-works'  },
    { label: 'Pricing',      hash: '#pricing'       },
  ];

  // Real routes, only relevant once logged in. Docs is admin-only — see
  // components/AdminRoute.jsx for the route-level guard that backs this up.
  const pageLinks = [
    { label: 'Chat',         to: '/chat' },
    { label: 'Projects',     to: '/projects' },
    { label: 'Chat History', to: '/chat/history' },
    ...(user?.role === 'admin' ? [{ label: 'Docs', to: '/docs' }] : []),
  ];

  const settingsLinks = [
    { label: 'My Info', to: '/settings/info' },
    { label: "KPI's",   to: '/settings/kpis' },
  ];

  const isPageLinkActive = (to) => {
    if (to === '/chat') return location.pathname.startsWith('/chat') && location.pathname !== '/chat/history';
    if (to === '/projects') return location.pathname.startsWith('/projects');
    return location.pathname === to;
  };

  return (
    <>
      <nav className={`navbar${scrolled ? ' navbar--scrolled' : ''}`}>
        <div className="container navbar__inner">
          <Link to={isAuthenticated ? '/' : '/login'} className="navbar__logo">
            <span className="navbar__logo-mark">M</span>
            <span className="navbar__logo-text">Margin<span className="gold-text">Pro</span></span>
          </Link>

          {isAuthenticated ? (
            <ul className="navbar__links">
              {sectionLinks.map((l) => (
                <li key={l.label}>
                  <a
                    href={l.hash}
                    className="navbar__link"
                    onClick={(e) => handleSectionLink(e, l.hash)}
                  >
                    {l.label}
                  </a>
                </li>
              ))}
              {pageLinks.map((l) => (
                <li key={l.label}>
                  <Link
                    to={l.to}
                    className={`navbar__link${isPageLinkActive(l.to) ? ' navbar__link--active' : ''}`}
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
              <li className="navbar__dropdown" ref={settingsRef}>
                <button
                  className={`navbar__link navbar__dropdown-btn${isSettingsPath ? ' navbar__link--active' : ''}`}
                  onClick={() => setSettingsOpen((v) => !v)}
                  aria-expanded={settingsOpen}
                >
                  Settings <ChevronDownIcon />
                </button>
                {settingsOpen && (
                  <div className="navbar__dropdown-menu">
                    {settingsLinks.map((l) => (
                      <Link
                        key={l.to}
                        to={l.to}
                        className={`navbar__dropdown-item${location.pathname === l.to ? ' navbar__dropdown-item--active' : ''}`}
                      >
                        {l.label}
                      </Link>
                    ))}
                  </div>
                )}
              </li>
            </ul>
          ) : (
            <span className="navbar__dev-badge">In development</span>
          )}

          <div className="navbar__cta">
            {isAuthenticated ? (
              <>
                {user?.firstName && (
                  <Link to="/settings/info" className="navbar__user-name navbar__user-name--link">
                    Hi, {user.firstName}
                  </Link>
                )}
                <button className="navbar__btn navbar__btn--ghost" onClick={handleLogout}>
                  Log out
                </button>
              </>
            ) : (
              <>
                <Link to="/signup" className="navbar__btn navbar__btn--ghost">Sign up</Link>
                <Link to="/login" className="navbar__btn navbar__btn--gold">Log in</Link>
              </>
            )}
          </div>

          <button
            className={`navbar__hamburger${drawerOpen ? ' is-open' : ''}`}
            onClick={() => setDrawerOpen(!drawerOpen)}
            aria-label="Toggle menu"
            aria-expanded={drawerOpen}
          >
            <span /><span /><span />
          </button>
        </div>
      </nav>

      <div className={`drawer${drawerOpen ? ' drawer--open' : ''}`} aria-hidden={!drawerOpen}>
        <div className="drawer__backdrop" onClick={() => setDrawerOpen(false)} />
        <div className="drawer__panel">
          <div className="drawer__header">
            <Link to={isAuthenticated ? '/' : '/login'} className="navbar__logo">
              <span className="navbar__logo-mark">M</span>
              <span className="navbar__logo-text">Margin<span className="gold-text">Pro</span></span>
            </Link>
            <button className="drawer__close" onClick={() => setDrawerOpen(false)} aria-label="Close menu">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
          <nav className="drawer__nav">
            {isAuthenticated && sectionLinks.map((l) => (
              <a
                key={l.label}
                href={l.hash}
                className="drawer__link"
                onClick={(e) => handleSectionLink(e, l.hash)}
              >
                {l.label}
              </a>
            ))}
            {isAuthenticated && pageLinks.map((l) => (
              <Link key={l.label} to={l.to} className="drawer__link">
                {l.label}
              </Link>
            ))}
            {isAuthenticated && (
              <>
                <span className="drawer__group-label">Settings</span>
                {settingsLinks.map((l) => (
                  <Link key={l.to} to={l.to} className="drawer__link drawer__link--sub">
                    {l.label}
                  </Link>
                ))}
              </>
            )}
          </nav>
          <div className="drawer__footer">
            {isAuthenticated ? (
              <button className="navbar__btn navbar__btn--ghost drawer__full-btn" onClick={handleLogout}>
                Log out
              </button>
            ) : (
              <>
                <Link to="/signup" className="navbar__btn navbar__btn--ghost drawer__full-btn">Sign up</Link>
                <Link to="/login" className="navbar__btn navbar__btn--gold drawer__full-btn">Log in</Link>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
